import { api } from '../api';

/**
 * A exportação completa — as chamadas, com o caminho escrito.
 *
 * Mesma razão dos outros arquivos de `api/`: o caminho é o contrato entre
 * a tela e a API, e o teste `26` procura o literal aqui.
 */

export interface Inventario {
  tabelas: { tabela: string; linhas: number }[];
  totalTabelas: number;
  totalLinhas: number;
  arquivos: number;
  arquivosBytes: number;
  migracaoAte: string | null;
}

export interface Exportacao {
  id: string;
  pedida_por_nome: string | null;
  pedida_em: string;
  situacao: string;
  migracao_ate: string | null;
  tabelas: number | null;
  linhas: string | null;
  arquivos: number | null;
  arquivos_bytes: string | null;
  arquivo_id: string | null;
  hash_sha256: string | null;
  tamanho_bytes: string | null;
  concluida_em: string | null;
  erro: string | null;
  observacoes: string | null;
  pacote: string | null;
}

export const verInventario = () => api<Inventario>('/exportacao/inventario');

export const verExportacoes = () => api<Exportacao[]>('/exportacao');

/**
 * Gerar o pacote.
 *
 * Sem `tentativas`: reenviar uma exportação porque a resposta demorou
 * geraria um segundo pacote de gigabytes. A chave da intenção (`0144`)
 * cobre o toque duplo; o que ela não deve cobrir é a paciência curta, e
 * por isso a tela desabilita o botão enquanto o pacote sai.
 */
export const gerarExportacao = (corpo: { observacoes?: string }) =>
  api<Exportacao>('/exportacao', { metodo: 'POST', corpo, tentativas: 0 });

/* ── A cópia de segurança diária — onda 297 · RN-244 ─────────────────── */

export interface CopiaGuardada {
  nome: string;
  verificada: boolean;
  bytes: number;
  criadaEm: string | null;
  migracaoAte: string | null;
  linhas: number | null;
}

export interface UltimaCopia {
  copia: string | null;
  iniciadaEm: string;
  terminadaEm: string | null;
  duracaoS?: number;
  situacao: 'ok' | 'atencao' | 'falha';
  motivos: { nivel: 'falha' | 'atencao'; texto: string }[];
  banco: {
    bytes: number | null; sha256: string | null; tabelas?: number; linhas?: number;
    migracaoAte?: string | null; servidor?: string;
  } | null;
  restauracao: {
    ok: boolean; erro: string | null; tabelasConferidas?: number;
    linhasConferidas?: number; duracaoS?: number;
  } | null;
  arquivos: {
    balde: string; objetos: number; bytes: number; novos: number; novosBytes: number;
    substituidos: number; copiados?: number; semCopia: number; hashDivergente: number;
    interrompido: string | null;
  } | null;
  disco: { livreBytes: number; totalBytes: number; limiteBytes: number } | null;
  copias: CopiaGuardada[];
  removidas: string[];
  escritorio?: EnvioAoEscritorio | null;
}

/** O túnel até o escritório e a pasta do UNAS — onda 299 · RN-246. */
export interface EstadoDoEscritorio {
  verificadoEm: string;
  ativo: boolean;
  instalando?: boolean;
  erro: string | null;
  tunel: {
    conectado: boolean; ikeEstabelecida: boolean; ipEscritorio: string | null;
    conectadoHaS: number | null; bytesEntrada: number | null; bytesSaida: number | null;
  };
  unas: {
    ip: string; protocolo: string; compartilhamento: string; subpasta: string;
    usuario: string; caminho: string;
  };
  unifi: {
    tipo: string; ipDoServidor: string; redeRemota: string; redeLocal: string;
    ike: string; criptografia: string; hash: string; grupoDh: string; pfs: boolean;
    vidaIkeS: number; vidaEspS: number; portasDoServidor: string; portaNoUnas: string;
  };
  primeiraCopiaFeita: boolean;
  segredosPendentes: boolean;
  temSegredos: boolean;
}

export interface EnvioAoEscritorio {
  situacao: 'ok' | 'falha' | 'aguardando' | 'desligada' | 'nao-enviada';
  destino?: string | null;
  enviadoEm?: string | null;
  arquivosEnviados?: number | null;
  bytesEnviados?: number | null;
  verificado?: boolean;
  /** 'noite': só o que foi naquela noite; 'completa': tudo (a cada 30 dias). */
  conferencia?: 'noite' | 'completa' | null;
  conferidos?: number | null;
  erro: string | null;
  ultimoSucessoEm: string | null;
  ultimaConferenciaCompletaEm?: string | null;
}

export interface EstadoDasCopias {
  destino: string;
  escritorio: EstadoDoEscritorio | null;
  ultimo: UltimaCopia | null;
  historico: {
    copia: string; terminadaEm: string; situacao: string; motivos: string[];
    verificada: boolean; bancoBytes: number | null; arquivosNovos: number | null;
  }[];
  avisos: { nivel: 'critico' | 'atencao'; texto: string }[];
  lidoEm: string;
}

export const verCopias = () => api<EstadoDasCopias>('/exportacao/copias');

/**
 * A chave do túnel e a senha do UNAS — só para quem gera a exportação, e só
 * enquanto a primeira cópia não chegou ao escritório (depois, 404).
 */
export const verSegredosDoEscritorio = () =>
  api<{ chaveDoTunel: string; senhaDoUnas: string | null }>('/exportacao/copias/escritorio/segredos');
